import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import AddIcon from "@mui/icons-material/Add";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { useAuth } from "@/auth/useAuth";
import { fetchLeads } from "@/api/leads";
import { fetchBranches } from "@/api/branches";
import { LEAD_STAGES_ORDER, LEAD_STAGE_LABELS, LEAD_SOURCE_LABELS } from "@/types/enums";
import type { Lead } from "@/types/lead";
import { formatDate } from "@/utils/format";
import { LeadCard } from "./LeadCard";
import { ConvertLeadDialog } from "./ConvertLeadDialog";
import { LeadEditDialog } from "./LeadEditDialog";

function mostFrequent(values: string[]): string | null {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  let best: string | null = null;
  let bestCount = 0;
  for (const [v, c] of counts) {
    if (c > bestCount) {
      best = v;
      bestCount = c;
    }
  }
  return best;
}

export function LeadsPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("leads.edit");

  const [branchId, setBranchId] = useState("");
  const [view, setView] = useState<"kanban" | "table">("kanban");
  const [convertingLead, setConvertingLead] = useState<Lead | null>(null);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const { data, isLoading, error } = useQuery({
    queryKey: ["leads", branchId],
    queryFn: () => fetchLeads({ branchId: branchId || undefined, limit: 200 }),
  });

  const allLeads = data?.items ?? [];
  const overdueLeads = allLeads.filter(
    (lead) =>
      lead.nextContactDate &&
      new Date(lead.nextContactDate) < new Date(new Date().toDateString()) &&
      ["NEW", "CALLED", "BOOKED"].includes(lead.stage),
  );

  const wonCount = allLeads.filter((l) => l.stage === "WON").length;
  const lostCount = allLeads.filter((l) => l.stage === "LOST").length;
  const todayCount = allLeads.filter((l) => l.nextContactDate?.slice(0, 10) === new Date().toISOString().slice(0, 10)).length;
  const conversionPercent = allLeads.length ? Math.round((wonCount / allLeads.length) * 100) : 0;
  const topLostReason = mostFrequent(allLeads.filter((l) => l.lostReason).map((l) => l.lostReason as string));

  const bySource = Object.entries(LEAD_SOURCE_LABELS)
    .map(([source, label]) => {
      const sourceLeads = allLeads.filter((l) => l.source === source);
      return { label, count: sourceLeads.length };
    })
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count);

  const byWeek = Array.from({ length: 8 }, (_, i) => {
    const end = new Date();
    end.setDate(end.getDate() - (7 - i) * 7);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    const count = allLeads.filter((l) => {
      const created = new Date(l.createdAt);
      return created >= start && created <= end;
    }).length;
    return { label: `${start.getDate()}.${start.getMonth() + 1}`, count };
  });

  return (
    <>
      <PageHeader
        title="Заявки"
        subtitle="Воронка заявок (лидов)"
        actions={
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" onClick={() => setView((v) => (v === "kanban" ? "table" : "kanban"))}>
              {view === "kanban" ? "Таблицей" : "Доской"}
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingLead(null);
                  setEditOpen(true);
                }}
              >
                Новая заявка
              </Button>
            )}
          </Stack>
        }
      />

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Всего заявок" value={String(allLeads.length)} hint="за всё время" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Зачислено" value={String(wonCount)} hint={`конверсия ${conversionPercent}%`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Связаться сегодня" value={String(todayCount)} hint={overdueLeads.length ? `${overdueLeads.length} просрочено` : "всё вовремя"} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Отказы" value={String(lostCount)} hint={topLostReason ? `частая причина: ${topLostReason}` : "нет"} />
        </Grid>
      </Grid>

      <Paper className="crm-page-filter" variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
          <TextField select label="Филиал" size="small" value={branchId} onChange={(e) => setBranchId(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">Все филиалы</MenuItem>
            {(branches ?? []).map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {overdueLeads.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "primary.main" }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Просроченные контакты · {overdueLeads.length}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {overdueLeads.slice(0, 8).map((lead) => (
              <Chip
                key={lead.id}
                label={`${lead.fullName} · ${lead.nextContactDate ? formatDate(lead.nextContactDate) : ""}`}
                onClick={() => {
                  setEditingLead(lead);
                  setEditOpen(true);
                }}
                clickable
              />
            ))}
          </Stack>
        </Paper>
      )}

      <QueryState isLoading={isLoading} error={error}>
        {view === "kanban" ? (
          <Box className="crm-leads-board" display="flex" gap={2} overflow="auto" pb={2}>
            {LEAD_STAGES_ORDER.map((stage) => {
              const stageLeads = allLeads.filter((lead) => lead.stage === stage);
              return (
                <Box className="crm-leads-board__column" key={stage} minWidth={280} flexShrink={0}>
                  <Paper className="crm-leads-board__stage" variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {LEAD_STAGE_LABELS[stage]}
                      </Typography>
                      <Chip size="small" label={stageLeads.length} />
                    </Stack>
                    {stageLeads.slice(0, 12).map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        onOpen={() => {
                          setEditingLead(lead);
                          setEditOpen(true);
                        }}
                      />
                    ))}
                    {stageLeads.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Нет заявок
                      </Typography>
                    )}
                  </Paper>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Paper className="crm-data-panel" variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Имя</TableCell>
                  <TableCell>Телефон</TableCell>
                  <TableCell align="right">Возраст</TableCell>
                  <TableCell>Предмет</TableCell>
                  <TableCell>Источник</TableCell>
                  <TableCell>Этап</TableCell>
                  <TableCell>Пробный</TableCell>
                  <TableCell>Следующий контакт</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {allLeads
                  .slice()
                  .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
                  .map((lead) => (
                  <TableRow key={lead.id} hover>
                    <TableCell>
                      <b>{lead.fullName}</b>
                    </TableCell>
                    <TableCell>{lead.phone}</TableCell>
                    <TableCell align="right">{lead.age ?? "—"}</TableCell>
                    <TableCell>{lead.subjectName ?? "—"}</TableCell>
                    <TableCell>{LEAD_SOURCE_LABELS[lead.source]}</TableCell>
                    <TableCell>
                      <StatusChip label={LEAD_STAGE_LABELS[lead.stage]} />
                    </TableCell>
                    <TableCell>{lead.trialDate ? `${formatDate(lead.trialDate)} ${lead.trialTime ?? ""}` : "—"}</TableCell>
                    <TableCell>
                      <Typography component="span" variant="body2" color={lead.overdue ? "error.main" : "inherit"} fontWeight={lead.overdue ? 600 : 400}>
                        {lead.nextContactDate ? formatDate(lead.nextContactDate) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => {
                          setEditingLead(lead);
                          setEditOpen(true);
                        }}
                      >
                        Открыть
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {allLeads.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Заявок нет.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )}
      </QueryState>

      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Источники заявок
            </Typography>
            <ResponsiveContainer width="100%" height={Math.max(160, bySource.length * 36)}>
              <BarChart data={bySource} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="label" width={120} />
                <Tooltip />
                <Bar dataKey="count" fill="#2E5AAC" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Заявки по неделям
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={byWeek}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#2E5AAC" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <ConvertLeadDialog lead={convertingLead} onClose={() => setConvertingLead(null)} />
      {canEdit && (
        <LeadEditDialog
          lead={editingLead}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onConvert={(lead) => setConvertingLead(lead)}
        />
      )}
    </>
  );
}
